/**
 * useThreads — Hybrid chat threading state management. Sprint T1c / T1c-FIX.
 *
 * Manages thread list, active thread, per-thread messages, send, archive, and auto-archive.
 * Sort order (per CHAT_ARCHITECTURE.md §4):
 *   1. Global thread always first
 *   2. Pinned threads by pinned_at ASC (oldest pin leftmost)
 *   3. Unpinned non-global threads by last_active_at DESC (most recent first)
 *
 * See: docs/architecture/CHAT_ARCHITECTURE.md §3, §4, §10
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient, ApiError } from '../lib/apiClient';

const API = '/api/v1/aif/chat/threads';
const MAX_ACTIVE_UNPINNED_TABS = 5;

// ── Sort helper (exported for testing) ───────────────────────────────────────

export function _sortThreads(threads) {
  const global = threads.filter(t => t.context_type === 'global');
  const pinned = threads
    .filter(t => t.context_type !== 'global' && t.pinned)
    .sort((a, b) => new Date(a.pinned_at || 0) - new Date(b.pinned_at || 0));
  const unpinned = threads
    .filter(t => t.context_type !== 'global' && !t.pinned)
    .sort((a, b) => new Date(b.last_active_at || 0) - new Date(a.last_active_at || 0));
  return [...global, ...pinned, ...unpinned];
}

// ── Pure fetch helper ────────────────────────────────────────────────────────

async function _apiFetch(path, token, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    ...opts,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || `${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useThreads(token) {
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messagesByThread, setMessagesByThread] = useState({});
  const [sending, setSending] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // Track activeThreadId in ref so archive callback can read it without stale closure
  const activeThreadIdRef = useRef(activeThreadId);
  useEffect(() => { activeThreadIdRef.current = activeThreadId; }, [activeThreadId]);

  // Auto-clear toast after 4 seconds
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  // Load all non-archived threads for the current user (sorted)
  const loadThreads = useCallback(async () => {
    if (!token) return [];
    try {
      const { threads: list } = await _apiFetch('', token);
      const sorted = _sortThreads(list || []);
      setThreads(sorted);
      return sorted;
    } catch { return []; }
  }, [token]);

  // Idempotent create-or-get a thread; auto-archives excess unpinned tabs
  const ensureThread = useCallback(async (contextType, contextId = null, contextLabel = 'Allgemein') => {
    if (!token) return null;
    const body = { context_type: contextType, context_label: contextLabel };
    if (contextId) body.context_id = contextId;
    try {
      const r = await apiClient.post(API, body, { entityType: 'thread', queueOnOffline: true });
      const { thread } = r.data;

      let autoArchived = null;
      setThreads(prev => {
        const idx = prev.findIndex(t => t.id === thread.id);
        const base = idx >= 0
          ? prev.map((t, i) => i === idx ? thread : t)
          : [thread, ...prev];
        const sorted = _sortThreads(base);

        // Auto-archive oldest unpinned non-global if over limit
        const unpinnedNonGlobal = sorted.filter(t => t.context_type !== 'global' && !t.pinned);
        if (unpinnedNonGlobal.length > MAX_ACTIVE_UNPINNED_TABS) {
          // oldest = last by last_active_at (sorted DESC so it's at the end)
          autoArchived = unpinnedNonGlobal[unpinnedNonGlobal.length - 1];
          return sorted.filter(t => t.id !== autoArchived.id);
        }
        return sorted;
      });

      // Perform auto-archive API call outside setThreads (async side effect)
      if (autoArchived) {
        const archived = autoArchived;
        apiClient.delete(`${API}/${archived.id}`, { entityType: 'thread', queueOnOffline: true }).catch(() => {});
        setToastMessage(
          `Tab '${archived.context_label}' archiviert (max. ${MAX_ACTIVE_UNPINNED_TABS} aktiv)`
        );
      }

      return thread;
    } catch { return null; }
  }, [token]);

  // Load messages for a thread (lazy — only fetches if not cached)
  const loadMessages = useCallback(async (threadId) => {
    if (!token || !threadId) return;
    try {
      const { messages } = await _apiFetch(`/${threadId}/messages`, token);
      setMessagesByThread(prev => ({ ...prev, [threadId]: messages || [] }));
    } catch { /* ignore */ }
  }, [token]);

  // Switch to a different thread
  const switchToThread = useCallback((threadId) => {
    setActiveThreadId(threadId);
  }, []);

  // Auto-load messages when activeThreadId changes (if not cached)
  useEffect(() => {
    if (!activeThreadId || messagesByThread[activeThreadId] !== undefined) return;
    loadMessages(activeThreadId);
  }, [activeThreadId, messagesByThread, loadMessages]);

  // Send a message in the active thread (optimistic)
  const sendInThread = useCallback(async (content, uiContext = null) => {
    const tid = activeThreadId;
    if (!tid || !content.trim() || !token) return null;

    const tempId = `tmp-${Date.now()}`;
    const tempUserMsg = { id: tempId, role: 'user', content, cards: [], created_at: new Date().toISOString() };
    setMessagesByThread(prev => ({
      ...prev,
      [tid]: [...(prev[tid] || []), tempUserMsg],
    }));
    setSending(true);

    try {
      const { data } = await apiClient.post(`${API}/${tid}/messages`, { content, ui_context: uiContext }, { entityType: 'message', queueOnOffline: true });

      const assistantMsg = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: data.message,
        cards: data.cards || [],
        intent: data.intent,
        quick_replies: data.quick_replies || [],
        data_changed: !!data.data_changed,
        created_at: new Date().toISOString(),
      };

      setMessagesByThread(prev => ({
        ...prev,
        [tid]: [
          ...(prev[tid] || []).filter(m => m.id !== tempId),
          { ...tempUserMsg, id: `u-${Date.now()}` },
          assistantMsg,
        ],
      }));

      setThreads(prev => _sortThreads(prev.map(t =>
        t.id === tid
          ? { ...t, message_count: (t.message_count || 0) + 2, last_active_at: new Date().toISOString() }
          : t
      )));

      return data;
    } catch (err) {
      let errText = err?.message || 'Fehler aufgetreten.';
      if (err?.status === 402) errText = 'Nachrichtenlimit erreicht. Bitte auf Premium upgraden.';

      const errMsg = {
        id: `e-${Date.now()}`,
        role: 'assistant',
        content: errText,
        isError: true,
        isUpgrade: err?.status === 402,
        cards: [],
        created_at: new Date().toISOString(),
      };
      setMessagesByThread(prev => ({
        ...prev,
        [tid]: [
          ...(prev[tid] || []).filter(m => m.id !== tempId),
          { ...tempUserMsg, id: `u-${Date.now()}` },
          errMsg,
        ],
      }));
      return null;
    } finally {
      setSending(false);
    }
  }, [activeThreadId, token]);

  // Pin or unpin a thread; updates pinned_at for sort order
  const setPinned = useCallback(async (threadId, pinned) => {
    if (!token) return;
    try {
      const r = await apiClient.patch(`${API}/${threadId}`, { pinned }, { entityType: 'thread', queueOnOffline: true });
      const { thread } = r.data;
      setThreads(prev => _sortThreads(prev.map(t => t.id === threadId ? thread : t)));
    } catch { /* ignore */ }
  }, [token]);

  // Archive (soft-delete) a thread; switches to global if it was active
  const archiveThread = useCallback(async (threadId) => {
    if (!token) return;
    try {
      await apiClient.delete(`${API}/${threadId}`, { entityType: 'thread', queueOnOffline: true });
      setThreads(prev => {
        const next = prev.filter(t => t.id !== threadId);
        // If archived thread was active, switch to global thread
        if (activeThreadIdRef.current === threadId) {
          const globalThread = next.find(t => t.context_type === 'global');
          if (globalThread) setActiveThreadId(globalThread.id);
        }
        return next;
      });
    } catch { /* ignore */ }
  }, [token]);

  // Initialize on login: load threads, ensure global thread, set active
  useEffect(() => {
    if (!token) {
      setThreads([]);
      setActiveThreadId(null);
      setMessagesByThread({});
      return;
    }
    let cancelled = false;
    (async () => {
      const list = await loadThreads();
      if (cancelled) return;

      let globalThread = list.find(t => t.context_type === 'global');
      if (!globalThread) {
        globalThread = await ensureThread('global', null, 'Allgemein');
      }
      if (globalThread && !cancelled) {
        setActiveThreadId(globalThread.id);
        const { messages } = await _apiFetch(`/${globalThread.id}/messages`, token)
          .catch(() => ({ messages: [] }));
        if (!cancelled) {
          setMessagesByThread(prev => ({ ...prev, [globalThread.id]: messages || [] }));
        }
      }
    })();
    return () => { cancelled = true; };
  // Intentional: bootstrap global thread only when token changes (login/logout), not when
  // ensureThread/setActiveThreadId identities change (which would cause infinite loop).
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeMessages = messagesByThread[activeThreadId] || [];

  return {
    threads,
    activeThreadId,
    activeMessages,
    sending,
    toastMessage,
    ensureThread,
    sendInThread,
    switchToThread,
    setPinned,
    archiveThread,
    loadThreads,
  };
}
